import { useTheme } from '../../../hooks/useTheme';
import { dentalAsset } from '../asset';

interface DentalHeroProps {
  onRequestDemo: () => void;
}

const TRUST = [
  { icon: 'ri-hospital-line', label: 'Consultorios y clínicas' },
  { icon: 'ri-building-2-line', label: 'Centros multisucursal' },
  { icon: 'ri-shield-check-line', label: 'Seguros y ARS' },
  { icon: 'ri-map-pin-2-line', label: 'Santo Domingo, RD' },
];

export default function DentalHero({ onRequestDemo }: DentalHeroProps) {
  const { theme } = useTheme();
  const isLight = theme === 'light';

  const ink = isLight ? '#0a1628' : '#ffffff';
  const sub = isLight ? 'rgba(10,22,40,0.72)' : 'rgba(255,255,255,0.58)';

  return (
    <section
      className="relative pt-28 md:pt-32 pb-14 md:pb-20 px-4 sm:px-6 overflow-hidden"
      style={{
        background: isLight
          ? 'linear-gradient(160deg, #f6fafd 0%, #ffffff 45%, #eef8f6 100%)'
          : 'linear-gradient(160deg, #04101f 0%, #071b2e 50%, #04121d 100%)',
      }}
    >
      {/* Blobs decorativos */}
      <div
        className="absolute top-0 right-0 w-[620px] h-[620px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(6,182,212,0.10) 0%, transparent 70%)',
          transform: 'translate(30%, -30%)',
        }}
      />
      <div
        className="absolute bottom-0 left-0 w-[420px] h-[420px] rounded-full pointer-events-none"
        style={{
          background: 'radial-gradient(circle, rgba(20,184,166,0.09) 0%, transparent 70%)',
          transform: 'translate(-30%, 30%)',
        }}
      />

      <div className="max-w-7xl mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-center">
        {/* ── Texto ── */}
        <div>
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-[11px] sm:text-xs font-bold tracking-widest uppercase mb-5 font-body"
            style={{
              borderColor: 'rgba(6,182,212,0.35)',
              backgroundColor: 'rgba(6,182,212,0.10)',
              color: isLight ? '#0e7490' : '#22d3ee',
            }}
          >
            <i className="ri-tooth-line text-sm" aria-hidden="true" />
            Tecnología especializada para clínicas dentales
          </div>

          <h1
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-5 leading-[1.12]"
            style={{ color: ink }}
          >
            Cada paciente, cada tratamiento y{' '}
            <span
              style={{
                background: 'linear-gradient(100deg, #06b6d4 0%, #00cfff 45%, #14b8a6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              cada peso bajo control.
            </span>
          </h1>

          <p
            className="font-body text-sm sm:text-base lg:text-lg leading-relaxed max-w-xl mb-8"
            style={{ color: sub }}
          >
            InnoFlex Odontología conecta agenda, expediente clínico, odontograma, tratamientos,
            facturación, inventario y seguimiento de pacientes en una sola plataforma.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 mb-8">
            <button
              type="button"
              onClick={onRequestDemo}
              aria-label="Solicitar demostración de InnoFlex Odontología"
              className="flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-full font-body text-sm font-extrabold transition-all duration-200 hover:scale-[1.03] active:scale-[0.97] cursor-pointer whitespace-nowrap"
              style={{
                background: 'linear-gradient(135deg, #06b6d4 0%, #14b8a6 100%)',
                color: '#04101f',
                boxShadow: '0 8px 26px rgba(6,182,212,0.32)',
              }}
            >
              <i className="ri-calendar-schedule-line text-base" aria-hidden="true" />
              Solicitar demostración
            </button>

            <a
              href="#planes-odontologia"
              className="flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-full font-body text-sm font-bold transition-all duration-200 hover:scale-[1.03] cursor-pointer whitespace-nowrap border"
              style={{
                borderColor: isLight ? 'rgba(6,182,212,0.40)' : 'rgba(0,207,255,0.35)',
                backgroundColor: isLight ? 'rgba(6,182,212,0.07)' : 'rgba(0,207,255,0.08)',
                color: isLight ? '#0e7490' : '#22d3ee',
              }}
            >
              <i className="ri-price-tag-3-line text-base" aria-hidden="true" />
              Ver planes odontológicos
            </a>
          </div>

          {/* Confianza */}
          <ul className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {TRUST.map((item) => (
              <li
                key={item.label}
                className="flex items-center gap-1.5 font-body text-[11px] sm:text-xs font-semibold"
                style={{ color: isLight ? 'rgba(10,22,40,0.62)' : 'rgba(255,255,255,0.45)' }}
              >
                <i className={`${item.icon} text-sm`} style={{ color: '#14b8a6' }} aria-hidden="true" />
                {item.label}
              </li>
            ))}
          </ul>
        </div>

        {/* ── Captura real del sistema ── */}
        <div className="lg:pl-4">
          <figure
            className="rounded-2xl border overflow-hidden"
            style={{
              borderColor: isLight ? 'rgba(10,22,40,0.12)' : 'rgba(255,255,255,0.10)',
              background: isLight ? '#ffffff' : 'rgba(4,16,31,0.85)',
              boxShadow: isLight
                ? '0 20px 60px rgba(6,182,212,0.16), 0 4px 16px rgba(10,22,40,0.08)'
                : '0 24px 70px rgba(0,0,0,0.55), 0 0 40px rgba(6,182,212,0.10)',
            }}
          >
            <div
              className="flex items-center gap-2 px-4 py-2.5 border-b"
              style={{
                borderColor: isLight ? 'rgba(10,22,40,0.08)' : 'rgba(255,255,255,0.07)',
                background: isLight ? '#eef4f9' : 'rgba(255,255,255,0.03)',
              }}
            >
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#f472b6' }} />
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#fbbf24' }} />
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#14b8a6' }} />
              <span
                className="font-body text-[11px] ml-2 truncate"
                style={{ color: isLight ? 'rgba(10,22,40,0.62)' : 'rgba(255,255,255,0.55)' }}
              >
                InnoFlex Dental — Odontograma
              </span>
            </div>
            <img
              src={dentalAsset('odontologia/hero-odontograma.png')}
              width={1200}
              height={633}
              alt="Odontograma digital de InnoFlex Odontología con el registro de una pieza dental"
              fetchPriority="high"
              decoding="async"
              className="w-full h-auto block"
            />
          </figure>
          <p
            className="font-body text-[11px] mt-2.5 text-center"
            style={{ color: isLight ? 'rgba(10,22,40,0.62)' : 'rgba(255,255,255,0.45)' }}
          >
            Captura real del sistema · datos de demostración
          </p>
        </div>
      </div>
    </section>
  );
}
